
    import { mapState, mapMutations, mapActions } from 'vuex';
    import Cookie from 'js-cookie';
    import datalayer from '@/utils/datalayer';
    import { jobData } from '@/utils/jobData';

    import SiteTerms from '@/components/Partials/SiteTerms';
    import PoweredByC1 from '@/components/Partials/PoweredByC1';

    export default {
        name: 'LoginRegister',

        components: {
            SiteTerms,
            PoweredByC1,
        },

        props: {
            skipContinueBtn: {
                type: Boolean,
                default: false,
            },

            callbackMode: {
                type: Boolean,
                default: false,
            },

            applyOffsiteMode: {
                type: Boolean,
                default: false,
            },

            minHeight: {
                type: Number,
                default: 260,
            },

            loginRedirect: {
                type: [String, Object],
                default: '/discover',
            },

            disableLoginRedirect: {
                type: Boolean,
                default: false,
            },

            sitePosition: {
                type: String,
                default: 'login_signup_popup',
            },
        },

        data() {
            return {
                authiFrameHeight: 660,
                spinner: {
                    loading: true,
                },
            };
        },

        computed: {
            ...mapState({
                $_authOptions: state => state.auth.authOptions,
                $_job: state => state.job.job,
            }),

            adjustAuthIFrameStyle() {
                return {
                    height: `${this.authiFrameHeight}px`,
                    minHeight: `${this.minHeight}px`,
                };
            },
        },

        mounted() {
            this.handleCrossOrigin();
        },

        methods: {
            ...mapMutations('appInfo', {
                $_OPEN_SIDEBAR: 'OPEN_SIDEBAR',
            }),

            ...mapActions('me', {
                $_storeAllUserData: 'storeAllUserData',
                $_storeOnesignal: 'storeOnesignal',
            }),

            ...mapActions('eventTracking', {
                $_pushEvent: 'pushEvent',
            }),

            getAuthFrameSrc() {
                let authSrc = 'https://www.careerone.com.au';
                if (process.env.APP_ENV === 'local') {
                    authSrc = process.env.APP_URL;
                } else if (process.env.APP_ENV === 'staging') {
                    authSrc = 'https://staging.careerone.xyz';
                }

                let finalAuthFrameSrc = `${authSrc}/one-auth?hostname=${this.$hostname}`;

                if (this.$route.query.admin) {
                    finalAuthFrameSrc = `${finalAuthFrameSrc}&admin=1`;
                }

                if (this.applyOffsiteMode) {
                    finalAuthFrameSrc = `${finalAuthFrameSrc}&offsite=1`;
                }

                // Add $_authOptions in the query string
                if (this.$_authOptions) {
                    const queryString = Object.keys(this.$_authOptions).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(this.$_authOptions[key])}`).join('&');
                    finalAuthFrameSrc += `&auth_options=${queryString}`;
                }

                return finalAuthFrameSrc;
            },

            handleCrossOrigin() {
                window.addEventListener('message', (event) => {
                    const response = event.data;

                    this.authiFrameHeight = response.container_height;
                    if (response.frame_loaded) {
                        this.spinner.loading = false;
                    }

                    if (response.login_response) {
                        this.loginResponse(response.login_response);
                    }

                    if (response.continue_with_email) {
                        this.$emit('continueWithEmail');
                    }

                    if (response.user_exist) {
                        this.$emit('userExist');
                    }

                    if (response.offsite_redirect) {
                        this.$emit('offsiteRedirect');
                    }
                });
            },

            $_dataLayerAfterLogin(response, dataLayer = {}) {
                const data = Object.assign(dataLayer, {
                    seeker_id: response.data.id,
                    seeker_hash: response.data.email_hash,
                });
                this.$_pushEvent(data);
                datalayer(data);
            },

            $_storeUserAfterLogin(response) {
                this.$_storeAllUserData(response.data);
                Cookie.set(process.env.COOKIE_TOKEN_NAME, response.access_token, process.env.COOKIE_EXPIRY_TIME);
                this.$_pushOnesignal();
            },

            $_pushOnesignal() {
                if (Cookie.get(process.env.COOKIE_ONESIGNAL)) {
                    this.$_storeOnesignal({ push_id: Cookie.get(process.env.COOKIE_ONESIGNAL) }).then(() => {
                        Cookie.remove(process.env.COOKIE_ONESIGNAL);
                    });
                }
            },

            beforeLogin() {
                this.$emit('beforeLogin');
            },

            loginResponse(response) {
                const dataLayer = {
                    site_position: this.sitePosition,
                    event: response.event === 'register' ? 'seeker_account_creation' : 'seeker_account_log_in',
                    method: response.method,
                    ...jobData(this.$_job),
                };

                this.$_dataLayerAfterLogin(response, dataLayer);
                this.$_storeUserAfterLogin(response);

                if (!this.callbackMode && !this.disableLoginRedirect) {
                    this.handleAfterLoginRedirect();
                }

                this.$emit('afterLogin', response);
                this.$emit('promptSuccess');
            },

            loginLater(user) {
                this.$emit('loginLater', user);
            },

            handleAfterLoginRedirect() {
                const qs = this.$route.query;

                let routerPush = this.loginRedirect;

                if (typeof this.loginRedirect === 'string') {
                    routerPush = { path: this.loginRedirect };
                }

                if (qs.redirect && routerPush.path !== '_self' && routerPush.path !== '_none') {
                    const resolvedRouter = this.$router.resolve({ path: qs.redirect });
                    if (qs.redirect.startsWith('/account') || qs.redirect.startsWith('/onboarding') || qs.redirect.startsWith('/dashboard')) {
                        window.location.href = resolvedRouter.href;
                    } else {
                        routerPush = qs.redirect;
                        if (resolvedRouter.href !== '/') {
                            routerPush = { path: qs.redirect };
                        }
                        this.$router.push(routerPush);
                    }
                } else if (routerPush.path === '_self') {
                    window.location.reload();
                    const url = new URL(window.location.href);
                    url.searchParams.delete('utm_source');
                    window.location.href = url;
                } else if (routerPush.path === '_none') {
                    return null;
                } else {
                    this.$router.push(routerPush);
                }
            },
        },
    };
